import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { X, CheckCircle2, Loader2, Check, ArrowLeft, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { FORM_SECTIONS, CALENDLY_URL } from "../config";
import { trackMeta } from "../lib/metaPixel";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EASE = [0.22, 1, 0.36, 1];

const STEP_SUBTITLES = [
  "Let's start with the basics.",
  "Tell us where your business stands today.",
  "What's really going on behind the scenes.",
  "Last step — this is where we get you on the calendar.",
];

const NEXT_LABELS = ["Next · Business Profile", "Next · The Problem", "Next · Commitment"];

const inputCls =
  "w-full rounded-xl border border-neutral-200 bg-white px-4 py-3 text-[15px] font-medium text-[#0A0A0A] outline-none transition-all placeholder:text-neutral-400 focus:border-[#F25C05] focus:ring-4 focus:ring-[#F25C05]/15";

function OptionCard({ selected, onClick, label, desc, multi, testid }) {
  return (
    <button
      type="button"
      data-testid={testid}
      onClick={onClick}
      className={`flex items-center gap-3 rounded-xl border px-3.5 py-3 text-left transition-all ${
        selected
          ? "border-[#F25C05] bg-[#F25C05]/[0.06] ring-1 ring-[#F25C05]"
          : "border-neutral-200 bg-white hover:border-neutral-300"
      }`}
    >
      {multi ? (
        <span
          className={`flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-[5px] border-2 transition-colors ${
            selected ? "border-[#F25C05] bg-[#F25C05]" : "border-neutral-300"
          }`}
        >
          {selected && <Check className="h-3 w-3 text-white" strokeWidth={3.5} />}
        </span>
      ) : (
        <span
          className={`h-[16px] w-[16px] shrink-0 rounded-full border-2 bg-white transition-all ${
            selected ? "border-[5px] border-[#F25C05]" : "border-neutral-300"
          }`}
        />
      )}
      <span className="flex flex-col">
        <span className="text-[13px] font-semibold leading-snug sm:text-[13.5px]">{label}</span>
        {desc && <span className="mt-0.5 text-[11px] font-medium leading-snug text-neutral-500">{desc}</span>}
      </span>
    </button>
  );
}

function Field({ field, values, setValues }) {
  const f = field;
  const val = values[f.name];
  const gridCls = f.grid === 2 ? "grid grid-cols-2 gap-2.5" : "grid grid-cols-1 gap-2.5";

  return (
    <div id={`field-${f.name}`} className="flex flex-col gap-2">
      <label className="text-[13px] font-bold text-[#0A0A0A] sm:text-[13.5px]">
        {f.label}
        {f.required && <span className="text-[#F25C05]"> *</span>}
      </label>

      {f.type === "radio" ? (
        <div className={gridCls}>
          {f.options.map((opt, i) => {
            const optLabel = typeof opt === "string" ? opt : opt.label;
            const optDesc = typeof opt === "string" ? null : opt.desc;
            const isRevenue = f.name === "revenue";
            const selected = isRevenue ? val === i : val === optLabel;
            return (
              <OptionCard
                key={optLabel}
                testid={`lead-option-${f.name}-${i}`}
                selected={selected}
                label={optLabel}
                desc={optDesc}
                onClick={() => setValues((s) => ({ ...s, [f.name]: isRevenue ? i : optLabel }))}
              />
            );
          })}
        </div>
      ) : f.type === "checkbox" ? (
        <div className={gridCls}>
          {f.options.map((opt, i) => {
            const picked = val?.includes(opt) || false;
            return (
              <OptionCard
                key={opt}
                testid={`lead-option-${f.name}-${i}`}
                multi
                selected={picked}
                label={opt}
                onClick={() =>
                  setValues((s) => ({
                    ...s,
                    [f.name]: picked ? (s[f.name] || []).filter((v) => v !== opt) : [...(s[f.name] || []), opt],
                  }))
                }
              />
            );
          })}
        </div>
      ) : f.type === "textarea" ? (
        <textarea
          id={`lead-${f.name}`}
          data-testid={`lead-input-${f.name}`}
          rows={3}
          required={f.required}
          placeholder={f.placeholder}
          value={val ?? ""}
          onChange={(e) => setValues((s) => ({ ...s, [f.name]: e.target.value }))}
          className={`${inputCls} resize-none`}
        />
      ) : (
        <input
          id={`lead-${f.name}`}
          data-testid={`lead-input-${f.name}`}
          type={f.type}
          required={f.required}
          placeholder={f.placeholder}
          value={val ?? ""}
          onChange={(e) => setValues((s) => ({ ...s, [f.name]: e.target.value }))}
          className={inputCls}
        />
      )}
    </div>
  );
}

export default function LeadFormModal({ open, onClose }) {
  const [values, setValues] = useState({});
  const [step, setStep] = useState(0);
  const [dir, setDir] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (open) trackMeta("InitiateCheckout", { content_name: "Personal Call Form" });
  }, [open]);

  const total = FORM_SECTIONS.length;
  const firstName = (values.name || "").trim().split(/\s+/)[0] || "";
  const pct = Math.round(((step + 1) / total) * 100);

  const close = () => {
    onClose();
    setTimeout(() => {
      setDone(false);
      setValues({});
      setStep(0);
      setDir(1);
    }, 350);
  };

  const goTo = (n) => {
    setDir(n > step ? 1 : -1);
    setStep(n);
  };

  const missingInStep = (s) => {
    for (const f of FORM_SECTIONS[s].fields) {
      if (!f.required) continue;
      const v = values[f.name];
      const empty = Array.isArray(v) ? v.length === 0 : v === undefined || v === "" || v === -1;
      if (empty) return f;
    }
    return null;
  };

  const flagMissing = (f) => {
    toast.error(`Please answer: ${f.label}`);
    setTimeout(() => {
      const el = document.getElementById(`field-${f.name}`);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 60);
  };

  const handleNext = () => {
    const missing = missingInStep(step);
    if (missing) {
      flagMissing(missing);
      return;
    }
    goTo(step + 1);
  };

  const buildPayload = () => {
    const payload = {};
    for (const sec of FORM_SECTIONS) {
      for (const f of sec.fields) {
        if (f.name === "revenue") {
          payload.revenue_index = typeof values.revenue === "number" ? values.revenue : -1;
          payload.revenue_label = typeof values.revenue === "number" ? f.options[values.revenue] : "";
        } else {
          payload[f.name] = values[f.name] ?? (f.type === "checkbox" ? [] : "");
        }
      }
    }
    return payload;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    for (let s = 0; s < total; s++) {
      const missing = missingInStep(s);
      if (missing) {
        goTo(s);
        flagMissing(missing);
        return;
      }
    }
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/leads`, buildPayload());
      if (data.qualified && CALENDLY_URL) {
        window.open(CALENDLY_URL, "_blank");
        toast.success("Great fit! Opening the calendar to book your personal call.");
      } else {
        toast.success("Request received! Our team will reach out on WhatsApp shortly.");
      }
      trackMeta("Lead", { content_name: "Personal Call Request" });
      onClose();
      navigate("/completed", { state: { name: (values.name || "").trim() } });
    } catch (err) {
      toast.error(
        (err?.response?.data && err.response.data.detail) || "Something went wrong. Please try again."
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          key="lead-modal"
          data-testid="lead-modal"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
          className="fixed inset-0 z-50 flex items-end justify-center bg-black/60 backdrop-blur-md sm:items-center sm:p-4"
          onClick={close}
        >
          <motion.div
            initial={{ y: 80, opacity: 0, scale: 0.98 }}
            animate={{ y: 0, opacity: 1, scale: 1 }}
            exit={{ y: 80, opacity: 0, scale: 0.98 }}
            transition={{ type: "spring", damping: 26, stiffness: 300 }}
            onClick={(e) => e.stopPropagation()}
            className="relative max-h-[92vh] w-full overflow-y-auto rounded-t-[26px] bg-white shadow-2xl sm:max-w-[540px] sm:rounded-[26px]"
          >
            <button
              data-testid="lead-modal-close-button"
              onClick={close}
              className="absolute right-3.5 top-3.5 z-20 flex h-9 w-9 items-center justify-center rounded-full bg-neutral-100 text-neutral-500 transition-colors hover:bg-neutral-200"
              aria-label="Close"
            >
              <X className="h-4.5 w-4.5" />
            </button>

            {done ? (
              <div data-testid="lead-form-success" className="flex flex-col items-center px-6 py-12 text-center">
                <div className="flex h-16 w-16 items-center justify-center rounded-full bg-[#F25C05]/10">
                  <CheckCircle2 className="h-8 w-8 text-[#F25C05]" />
                </div>
                <h3 className="mt-5 text-[22px] font-extrabold tracking-tight">You're in!</h3>
                <p className="mt-2.5 max-w-[320px] text-[14.5px] font-medium leading-relaxed text-neutral-500">
                  Thanks{firstName ? `, ${firstName}` : ""}! We've received your details and will
                  reach out on WhatsApp shortly to schedule your personal call.
                </p>
              </div>
            ) : (
              <form data-testid="lead-form" onSubmit={handleSubmit} className="flex min-h-[480px] flex-col sm:min-h-[560px]">
                {/* Progress header */}
                <div className="sticky top-0 z-10 border-b border-neutral-100 bg-white/95 px-5 pb-3.5 pt-5 backdrop-blur sm:px-8">
                  <div className="flex items-center gap-3">
                    {step > 0 && (
                      <button
                        type="button"
                        data-testid="lead-back-button"
                        onClick={() => goTo(step - 1)}
                        className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-neutral-200 text-neutral-500 transition-colors hover:border-neutral-300 hover:text-neutral-700"
                        aria-label="Back"
                      >
                        <ArrowLeft className="h-4 w-4" />
                      </button>
                    )}
                    {step === 0 && <div className="w-1 shrink-0" />}
                    <div className="flex-1">
                      <div className="mb-1.5 flex items-center justify-between">
                        <span className="text-[10.5px] font-bold uppercase tracking-[0.14em] text-neutral-400">
                          Step {step + 1} of {total}
                        </span>
                        <span className="text-[10.5px] font-bold text-[#F25C05]">{pct}%</span>
                      </div>
                      <div className="flex gap-1.5">
                        {FORM_SECTIONS.map((_, i) => (
                          <div key={i} className="h-1.5 flex-1 overflow-hidden rounded-full bg-neutral-100">
                            <motion.div
                              className="h-full rounded-full bg-gradient-to-r from-[#FF8A3C] to-[#F25C05]"
                              initial={false}
                              animate={{ width: i < step ? "100%" : i === step ? "50%" : "0%" }}
                              transition={{ duration: 0.45, ease: EASE }}
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Step content */}
                <AnimatePresence mode="wait" custom={dir}>
                  <motion.div
                    key={step}
                    custom={dir}
                    initial={{ opacity: 0, x: 36 * dir }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -36 * dir }}
                    transition={{ duration: 0.32, ease: EASE }}
                    className="flex flex-1 flex-col gap-4 px-5 pt-5 sm:px-8"
                  >
                    <div>
                      <h3 className="text-[20px] font-extrabold tracking-tight">
                        {FORM_SECTIONS[step].title.replace(/^\d+\.\s*/, "")}
                      </h3>
                      <p className="mt-0.5 text-[13px] font-medium text-neutral-500">{STEP_SUBTITLES[step]}</p>
                    </div>
                    {FORM_SECTIONS[step].fields.map((f) => (
                      <Field key={f.name} field={f} values={values} setValues={setValues} />
                    ))}
                  </motion.div>
                </AnimatePresence>

                {/* Sticky action bar */}
                <div className="sticky bottom-0 mt-auto bg-gradient-to-t from-white via-white/95 to-transparent px-5 pb-5 pt-8 sm:px-8">
                  {step < total - 1 ? (
                    <button
                      type="button"
                      data-testid="lead-next-button"
                      onClick={handleNext}
                      className="flex w-full items-center justify-center gap-2 rounded-[14px] bg-gradient-to-b from-[#FF8A3C] to-[#F25C05] py-4 text-[15.5px] font-bold text-white shadow-[0_12px_28px_-8px_rgba(242,92,5,0.55)] transition-all hover:shadow-[0_16px_34px_-8px_rgba(242,92,5,0.7)] active:scale-[0.985]"
                    >
                      {NEXT_LABELS[step]} <ArrowRight className="h-4.5 w-4.5" />
                    </button>
                  ) : (
                    <button
                      type="submit"
                      data-testid="lead-form-submit-button"
                      disabled={submitting}
                      className="flex w-full items-center justify-center gap-2 rounded-[14px] bg-gradient-to-b from-[#FF8A3C] to-[#F25C05] py-4 text-[15.5px] font-bold text-white shadow-[0_12px_28px_-8px_rgba(242,92,5,0.55)] transition-all hover:shadow-[0_16px_34px_-8px_rgba(242,92,5,0.7)] active:scale-[0.985] disabled:opacity-70"
                    >
                      {submitting ? (
                        <>
                          <Loader2 className="h-4.5 w-4.5 animate-spin" /> Sending…
                        </>
                      ) : (
                        <>
                          {firstName ? `${firstName}, ` : ""}Request My Personal Call
                          <ArrowRight className="h-4.5 w-4.5" />
                        </>
                      )}
                    </button>
                  )}
                </div>
              </form>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
